import CloseIcon from "@mui/icons-material/CloseRounded";
import { Alert, Box, Button, IconButton, Slide, Stack } from "@mui/material";
import { type Toast, useToastStore } from "@stores/toastStore";
import { useEffect } from "react";

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    if (!toast.duration) return;
    const id = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(id);
  }, [toast.id, toast.duration, dismiss]);

  return (
    <Slide direction="left" in mountOnEnter unmountOnExit>
      <Alert
        severity={toast.severity}
        variant="standard"
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {toast.action && (
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => {
                  toast.action?.onClick();
                  dismiss(toast.id);
                }}
                sx={{ fontWeight: 600, minWidth: 0 }}
              >
                {toast.action.label}
              </Button>
            )}
            <IconButton
              size="small"
              aria-label="Dismiss notification"
              onClick={() => dismiss(toast.id)}
              sx={{ color: "inherit" }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        }
        sx={{
          minWidth: 280,
          maxWidth: 420,
          boxShadow: 4,
          alignItems: "center",
        }}
      >
        {toast.message}
      </Alert>
    </Slide>
  );
}

/**
 * Global toast host — renders the stack of active notifications in the
 * bottom-right corner. Mounted once at the root via <AppProviders />.
 */
export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <Stack
      component="section"
      spacing={1}
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: (t) => t.zIndex.snackbar,
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
      }}
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </Stack>
  );
}
